import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  Activity, 
  Radio, 
  Plus, 
  CreditCard, 
  Settings, 
  Map, 
  MessageSquare, 
  LifeBuoy,
  ChevronRight,
  Menu as MenuIcon,
  X,
  Globe,
  Home,
  FileText
} from 'lucide-react';
import clsx from 'clsx';

const SidebarSection = ({ title, children, collapsed }) => (
  <div className="mb-6">
    {!collapsed && (
      <div className="px-6 mb-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
        {title}
      </div>
    )}
    <div className="space-y-0.5">
      {children}
    </div>
  </div>
);

const SidebarItem = ({ to, icon: Icon, label, active, collapsed, badge }) => (
  <Link to={to}>
    <div
      className={clsx(
        "flex items-center gap-3 px-4 py-2 mx-2 rounded-lg transition-all duration-200 group relative",
        active 
          ? "bg-white/10 text-white" 
          : "text-slate-400 hover:bg-white/5 hover:text-white"
      )}
    >
      {active && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full" />
      )}
      <Icon size={18} className={clsx("relative z-10 transition-colors", active ? "text-primary" : "group-hover:text-white")} />
      {!collapsed && (
        <span className="relative z-10 text-sm font-medium whitespace-nowrap flex-1">{label}</span>
      )}
      {!collapsed && badge && (
        <span className="px-1.5 py-0.5 rounded-md bg-primary/20 text-primary text-[10px] font-bold">
          {badge}
        </span>
      )}
    </div>
  </Link>
);

export default function AppLayout({ children }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-white flex overflow-hidden font-sans">
      {/* Sidebar Desktop */}
      <motion.aside 
        initial={false}
        animate={{ width: collapsed ? 80 : 260 }}
        className="hidden md:flex flex-col z-20 h-screen border-r border-white/5 bg-surface/50 backdrop-blur-xl"
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center px-6 mb-4">
          <motion.div 
            animate={{ opacity: collapsed ? 0 : 1 }} 
            className="flex items-center gap-2 font-bold text-xl tracking-wider text-white"
          >
            {!collapsed ? "LuckStat" : "L"}
          </motion.div>
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <SidebarSection title="Кокпит" collapsed={collapsed}>
            <SidebarItem to="/" icon={Home} label="Обзор" active={location.pathname === '/'} collapsed={collapsed} />
          </SidebarSection>

          <SidebarSection title="Аналитика" collapsed={collapsed}>
            <SidebarItem to="/dashboard" icon={LayoutDashboard} label="Дашборд" active={location.pathname === '/dashboard'} collapsed={collapsed} />
            <SidebarItem to="/channel-analytics" icon={Activity} label="Аналитика канала" active={location.pathname === '/channel-analytics'} collapsed={collapsed} />
            <SidebarItem to="/content" icon={MessageSquare} label="Контент" active={location.pathname === '/content'} collapsed={collapsed} />
            <SidebarItem to="/audience" icon={Users} label="Аудитория" active={location.pathname === '/audience'} collapsed={collapsed} />
            <SidebarItem to="/events" icon={Radio} label="Лента событий" active={location.pathname === '/events'} collapsed={collapsed} />
            <SidebarItem to="/reports" icon={FileText} label="Отчеты" active={location.pathname === '/reports'} collapsed={collapsed} />
          </SidebarSection>

          <SidebarSection title="Источники" collapsed={collapsed}>
            <SidebarItem to="/channels" icon={Radio} label="Мои каналы" active={location.pathname === '/channels'} collapsed={collapsed} />
            <SidebarItem to="/add-source" icon={Plus} label="Добавить источник" collapsed={collapsed} />
          </SidebarSection>

          <SidebarSection title="Центр управления" collapsed={collapsed}>
            <SidebarItem to="/subscription" icon={CreditCard} label="Управление подпиской" collapsed={collapsed} />
            <SidebarItem to="/settings" icon={Settings} label="Настройки" active={location.pathname === '/settings'} collapsed={collapsed} />
            <SidebarItem to="/roadmap" icon={Map} label="Roadmap" collapsed={collapsed} />
            <SidebarItem to="/partners" icon={Users} label="Партнерская программа" collapsed={collapsed} />
            <SidebarItem to="/support" icon={LifeBuoy} label="Поддержка" collapsed={collapsed} />
          </SidebarSection>
        </div>

        {/* Collapse Button */}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="p-4 border-t border-white/5 text-slate-500 hover:text-white transition-colors flex justify-center"
        >
          {collapsed ? <ChevronRight size={20} /> : <div className="flex items-center gap-2 text-xs font-medium"><ChevronRight className="rotate-180" size={16} /> Свернуть меню</div>}
        </button>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col relative z-10 h-screen overflow-hidden bg-background">
        {/* Top App Header */}
        <header className="h-14 border-b border-white/5 bg-surface/30 flex items-center justify-between px-6">
          {/* Left: Breadcrumbs & Status */}
          <div className="flex items-center gap-6">
            <button className="md:hidden text-white" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </button>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="font-medium text-white">Free Flight</span>
              </div>
              <div className="h-4 w-[1px] bg-white/10"></div>
              <div className="text-slate-400">
                События: <span className="text-white">0 / 20 000</span>
              </div>
              <div className="text-slate-400">
                Сессии: <span className="text-white">0 / 100</span>
              </div>
            </div>
          </div>

          {/* Right: User Profile */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 px-3 py-1.5 rounded-full hover:bg-white/5 transition-colors cursor-pointer border border-transparent hover:border-white/10">
              <div className="text-right hidden sm:block">
                <div className="text-xs font-medium text-white">smolnikov.ev@yandex.ru</div>
                <div className="text-[10px] text-slate-400">USD</div>
              </div>
              <div className="w-8 h-8 rounded-full bg-surfaceLight flex items-center justify-center text-slate-400">
                <Globe size={16} />
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Page Content */}
        <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="max-w-[1600px] mx-auto pb-20"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-[280px] bg-surface z-50 md:hidden border-r border-white/10 flex flex-col"
            >
              <div className="h-16 flex items-center justify-between px-6 border-b border-white/5">
                <span className="font-bold text-xl tracking-wider text-white">LuckStat</span>
                <button onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white">
                  <X />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {/* Same menu structure as desktop */}
                <SidebarSection title="Аналитика" collapsed={false}>
                  <SidebarItem to="/" icon={LayoutDashboard} label="Дашборд" active={location.pathname === '/'} collapsed={false} />
                  <SidebarItem to="/audience" icon={Users} label="Аудитория" active={location.pathname === '/audience'} collapsed={false} />
                </SidebarSection>
                {/* ... Add other sections ... */}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
